import React, { useEffect, useMemo, useState } from "react";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import { appFetchJson } from "../../../utils/http";
import apiRoutes from "../../../api/apiRoutes";

import CiudadAutocomplete from "../../../components/autocomplete/CiudadAutocomplete";

// Opciones fijas según EessFlotaEnum
const FLOTA_OPTIONS = [
  { value: "NO", label: "NO" },
  { value: "SI", label: "SI" },
];

export default function EditarEstacion({ id, onClose, onVolver, onSaved }) {
  const closeFn = onClose || onVolver || (() => {});
  const savedFn = onSaved || (() => {});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [original, setOriginal] = useState(null);

  // =========================
  // READ ONLY
  // =========================
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [codigoCliente, setCodigoCliente] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono1, setTelefono1] = useState("");
  const [telefono2, setTelefono2] = useState("");
  const [fechaAlta, setFechaAlta] = useState("");
  const [ruc, setRuc] = useState("");
  const [verificador, setVerificador] = useState("");
  const [cantEstaciones, setCantEstaciones] = useState("");
  const [zona, setZona] = useState("");

  // =========================
  // EDITABLES
  // =========================
  const [flota, setFlota] = useState("");
  const [ciudad, setCiudad] = useState(null); // {id, nombre, codigo?, nombrePais?}
  const [email, setEmail] = useState("");
  const [latitud, setLatitud] = useState("");
  const [longitud, setLongitud] = useState("");
  const [flete, setFlete] = useState("");
  const [porcAlquiler, setPorcAlquiler] = useState("");

  // helper: considera 0 como válido
  const hasValue = (v) => v !== null && v !== undefined;

  // =========================
  // CARGA REAL (GET /eess/{id})
  // =========================
  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await appFetchJson(apiRoutes.eess.detail(id));
        if (cancelled) return;

        setOriginal(data);

        // READ ONLY
        setCodigo(data.codigoEstacion ?? "");
        setNombre(data.nombreEstacion ?? "");
        setCodigoCliente(data.clienteCodigo ?? "");
        setDireccion(data.descricaoDireccion ?? "");
        setTelefono1(data.descricaoTelefono1 ?? "");
        setTelefono2(data.descricaoTelefono2 ?? "");
        setFechaAlta(data.dataAlta ?? "");
        setRuc(data.descricaoRuc ?? "");
        setVerificador(data.numeroVerificador ?? "");
        setCantEstaciones(data.cantidadEstaciones ?? "");
        setZona(data.zonaNombre ?? "");

        // EDITABLES
        setFlota(data.eessFlota ?? "");

        // ✅ IMPORTANTE: ciudadId=0 (DEFAULT) es válido
        if (hasValue(data.ciudadId)) {
          setCiudad({
            id: data.ciudadId, // puede ser 0
            nombre: data.ciudadNombre ?? "DEFAULT",
          });
        } else {
          setCiudad(null);
        }

        setEmail(data.descricaoEmail ?? "");
        setLatitud(data.latitud ?? "");
        setLongitud(data.longitud ?? "");
        setFlete(data.valorFlete ?? "");
        setPorcAlquiler(data.porcentajeAlquiler ?? "");
      } catch (e) {
        if (!cancelled) {
          console.error("Error al cargar estación:", e);
          setError("Error al cargar la estación");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // =========================
  // CHANGED
  // =========================
  const changed = useMemo(() => {
    if (!original) return false;

    return (
      flota !== (original.eessFlota ?? "") ||
      // ✅ comparar ciudadId (incluye 0)
      (ciudad?.id ?? null) !== (hasValue(original.ciudadId) ? original.ciudadId : null) ||
      email !== (original.descricaoEmail ?? "") ||
      String(latitud) !== String(original.latitud ?? "") ||
      String(longitud) !== String(original.longitud ?? "") ||
      String(flete) !== String(original.valorFlete ?? "") ||
      String(porcAlquiler) !== String(original.porcentajeAlquiler ?? "")
    );
  }, [original, flota, ciudad, email, latitud, longitud, flete, porcAlquiler]);

  // =========================
  // GUARDAR (PUT real)
  // =========================
  const handleSave = async () => {
    if (!changed || !id) return;

    setSaving(true);
    setError("");

    const payload = {
      id,
      eessFlota: flota || null,
      ciudadId: ciudad?.id ?? null, // incluye 0 (DEFAULT)
      descricaoEmail: email || null,
      latitud: latitud !== "" ? Number(latitud) : null,
      longitud: longitud !== "" ? Number(longitud) : null,
      valorFlete: flete !== "" ? Number(flete) : null,
      porcentajeAlquiler:
        porcAlquiler !== "" ? Number(porcAlquiler) : null,
    };

    try {
      await appFetchJson(
        apiRoutes.eess.editar(id), // 👈 nuevo endpoint
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      savedFn();
      closeFn();
    } catch (e) {
      console.error("Error al guardar estación:", e);
      setError("Error al guardar la estación");
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body">Cargando…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="section-title">Editar Estación</h2>
        </div>

        <div className="modal-body">
          {error && <div className="mb-3 text-red-600">{error}</div>}

          <div className="form-grid">
            <FilaRead label="Cod. EE.SS." value={codigo} />
            <FilaRead label="EE.SS." value={nombre} />
            <FilaRead label="Cod. Cliente" value={codigoCliente} />
            <FilaRead label="Dirección" value={direccion} />
            <FilaRead label="Teléfono 1" value={telefono1} />
            <FilaRead label="Teléfono 2" value={telefono2} />
            <FilaRead label="Fecha Alta" value={fechaAlta} />
            <FilaRead label="RUC" value={ruc} />
            <FilaRead label="Nro. Verificador" value={verificador} />
            <FilaRead label="Cant. Estaciones" value={cantEstaciones} />
            <FilaRead label="Zona" value={zona} />

            {/* === COMBO FLOTA === */}
            <FilaEdit
              label="EE.SS. Flota"
              input={
                <select
                  className="input"
                  value={flota || ""}
                  onChange={(e) => setFlota(e.target.value)}
                >
                  <option value="">--Seleccione--</option>
                  {FLOTA_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              }
            />

            {/* === AUTOCOMPLETE CIUDAD === */}
            <FilaEdit
              label="Ciudad"
              input={
                <CiudadAutocomplete
                  value={ciudad}
                  onChange={setCiudad}
                  placeholder="Buscar ciudad..."
                />
              }
            />

            <FilaEdit
              label="Email"
              input={
                <input
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="Latitud"
              input={
                <input
                  className="input"
                  value={latitud}
                  onChange={(e) => setLatitud(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="Longitud"
              input={
                <input
                  className="input"
                  value={longitud}
                  onChange={(e) => setLongitud(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="Flete"
              input={
                <input
                  className="input"
                  type="number"
                  value={flete}
                  onChange={(e) => setFlete(e.target.value)}
                />
              }
            />

            <FilaEdit
              label="% Alquiler"
              input={
                <input
                  className="input"
                  type="number"
                  value={porcAlquiler}
                  onChange={(e) => setPorcAlquiler(e.target.value)}
                />
              }
            />
          </div>
        </div>

        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar isValid={changed} saving={saving} onClick={handleSave} />
            <BtnCancelar onClick={closeFn} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Subcomponentes ===== */

function FilaRead({ label, value }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1 py-2 font-medium">
        {value !== null && value !== undefined && value !== "" ? (
          value
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </div>
    </div>
  );
}

function FilaEdit({ label, input }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1">{input}</div>
    </div>
  );
}
